class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    // Preserve subscription states before rendering new content
    const subscriptionStates = this.preserveSubscriptionStatesDrawer();
    
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
      
      // Restore subscription states after rendering
      this.restoreSubscriptionStatesDrawer(subscriptionStates);
    });
  }

  preserveSubscriptionStatesDrawer() {
    const subscriptionStates = new Map();
    
    try {
      // Save current subscription states from cart drawer
      this.querySelectorAll('.subscribe-checkbox').forEach(checkbox => {
        const itemIndex = checkbox.dataset.index;
        const isChecked = checkbox.checked;
        const selectElement = this.querySelector(`.subscribe-select[data-index="${itemIndex}"]`);
        const selectedPlan = selectElement ? selectElement.value : null;
        
        // Also check if this item currently has a subscription in the cart
        const cartRow = this.querySelector(`#CartDrawer-Item-${itemIndex}`);
        const hasExistingSubscription = cartRow && cartRow.querySelector('.selling-plan-details');
        
        subscriptionStates.set(itemIndex, {
          checked: isChecked,
          planId: selectedPlan,
          hasExistingSubscription: !!hasExistingSubscription
        });
      });
    } catch (error) {
      console.warn('Error preserving cart drawer subscription states:', error);
    }
    
    return subscriptionStates;
  }

  restoreSubscriptionStatesDrawer(subscriptionStates) {
    // Restore subscription states in cart drawer
    if (!subscriptionStates) return;
    
    try {
      subscriptionStates.forEach((state, itemIndex) => {
        const checkbox = this.querySelector(`.subscribe-checkbox[data-index="${itemIndex}"]`);
        const selectElement = this.querySelector(`.subscribe-select[data-index="${itemIndex}"]`);
        
        if (checkbox) {
          checkbox.checked = state.checked;
        }
        if (selectElement && state.planId) {
          selectElement.value = state.planId;
        }
      });
    } catch (error) {
      console.warn('Error restoring cart drawer subscription states:', error);
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  onCartUpdate() {
    // Preserve subscription states before cart drawer refresh
    const subscriptionStates = this.preserveSubscriptionStates();
    
    fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const sourceQty = html.querySelector('cart-drawer-items');
        if (sourceQty) {
          this.innerHTML = sourceQty.innerHTML;
        }
        
        // Restore subscription states after DOM update
        setTimeout(() => {
          this.restoreSubscriptionStates(subscriptionStates);
        }, 100);
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
